<template>
    <div>
        <div>
            <div class="app-address-info">
                <div>
                    <div class="app-input-address">
                        <div class="app-user-address">
                            <label :for="'addressline1' + addressDetails.addressCategoryId" class="sr-only">
                                Address line 1
                            </label>
                            <input type="text" :id="'addressline1' + addressDetails.addressCategoryId" class="user-address-info" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddress1 ? 'orange-bg' : '']"
                            v-model="physicalAddress1" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                        </div>
                        <div class="app-user-address">
                            <label :for="'addressline2' + addressDetails.addressCategoryId" class="sr-only">
                                Address line 2
                            </label>
                            <input type="text" :id="'addressline2' + addressDetails.addressCategoryId" class="user-address-info" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddress2 ? 'orange-bg' : '']"
                             v-model="physicalAddress2" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                        </div>
                        <div class="app-user-address">
                            <label :for="'city' + addressDetails.addressCategoryId" class="sr-only">
                                City
                            </label>
                            <input type="text" :id="'city' + addressDetails.addressCategoryId" class="user-address-info-city" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddressCity ? 'orange-bg' : '']"
                             v-model="physicalAddressCity" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                        </div>
                        <div class="app-user-address-info">
                            <div class="app-user-address">
                                <label :for="'state' + addressDetails.addressCategoryId" class="sr-only">
                                    State
                                </label>
                                <select class="app-custom-select" :id="'state' + addressDetails.addressCategoryId" aria-required="true" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddressState ? 'orange-bg' : '']"
                                    v-model="physicalAddressState" @focusout="checkMailingAddress" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                                    <option v-for="(type) in usStates" :value="type.stateId" :key="type.code">
                                        {{ type.code }} - {{ type.description }}
                                    </option>
                                </select>
                            </div>
                            <div class="app-user-address">
                                <label :for="'zipcode' + addressDetails.addressCategoryId" class="sr-only">
                                    Zipcode
                                </label>
                                <input type="text" :id="'zipcode' + addressDetails.addressCategoryId" class="zip-selection" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddressZipcode ? 'orange-bg' : '']"
                                v-model="physicalAddressZip" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                                <div class="diff">
                                    -
                                </div>
                                <div>
                                    <label :for="'zipplus' + addressDetails.addressCategoryId" class="sr-only">
                                        Zipplus
                                    </label>
                                    <input type="text" :id="'zipplus' + addressDetails.addressCategoryId" class="user-address-info-zipplus" :class="[{ 'user-address-info-gray': physicalMailingAddressCheck }, !userAddressZipplus ? 'orange-bg' : '']" 
                                    maxlength="4" v-model="physicalAddressZipPlus" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import * as COMMONS from '../../store/_commons';

const props = defineProps({
  addressDetails: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const router = useRouter();

// Reactive State
const physicalAddress1 = ref('');
const physicalAddress2 = ref('');
const physicalAddressCity = ref('');
const physicalAddressState = ref('');
const physicalAddressZip = ref('');
const physicalAddressZipPlus = ref('');

const mailingAddress1 = ref('');
const mailingAddress2 = ref('');
const mailingAddressCity = ref('');
const mailingAddressState = ref('');
const mailingAddressZip = ref('');

const physicalMailingAddressCheck = ref(true);

// Computed Properties 
const addressLookupInfo = computed(() => store.getters[COMMONS.GUEST_ADDRESS_LOOKUP_INFO]);
const usStates = computed(() => addressLookupInfo.value?.allUsStates || []);
const userPhysicalAddress = computed(() => store.state.guestLogin.userPhysicalAddress);
const userMailingAddress = computed(() => store.state.guestLogin.userMailingAddress);

const userAddress1 = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const addressLine1 = props.addressDetails?.addressLine1?.toUpperCase();
  if (props.addressDetails?.addressCategoryId == 1) {
    return addressLine1 == userPhysicalAddress.value?.addressLine1?.toUpperCase();
  }
  return addressLine1 == userMailingAddress.value?.addressLine1?.toUpperCase();
});

const userAddress2 = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const addressLine2 = props.addressDetails?.addressLine2?.toUpperCase();
  if (props.addressDetails?.addressCategoryId == 1) {
    return addressLine2 == userPhysicalAddress.value?.addressLine2?.toUpperCase();
  }
  return addressLine2 == userMailingAddress.value?.addressLine2?.toUpperCase();
});

const userAddressCity = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const city = props.addressDetails?.city?.toUpperCase();
  if (props.addressDetails?.addressCategoryId == 1) {
    return city == userPhysicalAddress.value?.city?.toUpperCase();
  }
  return city == userMailingAddress.value?.city?.toUpperCase();
});

const userAddressState = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const stateId = props.addressDetails?.stateId;
  if (props.addressDetails?.addressCategoryId == 1) {
    return stateId == userPhysicalAddress.value?.stateId;
  }
  return stateId == userMailingAddress.value?.stateId;
});

const userAddressZipcode = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const zipCode = props.addressDetails?.zipCode;
  if (props.addressDetails?.addressCategoryId == 1) {
    return zipCode == userPhysicalAddress.value?.zipCode;
  }
  return zipCode == userMailingAddress.value?.zipCode;
});

const userAddressZipplus = computed(() => {
  if (store.state.guestLogin.addressUpdateStep != 2) return true;
  const zipPlus = props.addressDetails?.zipPlus;
  if (props.addressDetails?.addressCategoryId == 1) {
    return zipPlus == userPhysicalAddress.value?.zipPlus;
  }
  return zipPlus == userMailingAddress.value?.zipPlus;
});

// Watcher
watch(() => props.addressDetails, (newVal) => {
  if (newVal) {
    physicalAddress1.value = newVal.addressLine1;
    physicalAddress2.value = newVal.addressLine2;
    physicalAddressCity.value = newVal.city;
    physicalAddressState.value = newVal.stateId;
    physicalAddressZip.value = newVal.zipCode;
    physicalAddressZipPlus.value = newVal.zipPlus;
  }
}, { deep: true, immediate: true });

// Lifecycle Hook
onMounted(() => {
  window.scrollTo(0, 0);
  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");
  document.getElementById("app").scrollTop = 0;

  if (props.addressDetails != null) {
    physicalAddress1.value = props.addressDetails.addressLine1;
    physicalAddress2.value = props.addressDetails.addressLine2;
    physicalAddressCity.value = props.addressDetails.city;
    physicalAddressState.value = props.addressDetails.stateId;
    physicalAddressZip.value = props.addressDetails.zipCode;
    physicalAddressZipPlus.value = props.addressDetails.zipPlus;
  }
});

// Methods

// eslint-disable-next-line no-unused-vars
const handleRouting = (info) => {
  if (info == 'previous') {
    store.state.guestLogin.addressUpdateStep = 2;
  } else if (info == 'next') {
    router.push({ path: '/profile' });
  }
};

const checkMailingAddress = () => {
  if (physicalMailingAddressCheck.value) {
    mailingAddress1.value = physicalAddress1.value;
    mailingAddress2.value = physicalAddress2.value;
    mailingAddressCity.value = physicalAddressCity.value;
    mailingAddressState.value = physicalAddressState.value;
    mailingAddressZip.value = physicalAddressZip.value;
  }
};
</script>


<style scoped lang="scss">

.app-address-info{
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 1rem;
  .app-input-address{
    display: block;
    .app-user-address-info{
      display: flex;
      gap: 2rem;
    }
    .app-user-address{
      margin-bottom: 1rem;
      display: flex;
      .user-address-info{
        min-width: 275px;
        width: 100%;
      }
      .diff{
        padding: 0.5rem;
      }
      .zip-selection {
        width: 106px !important;
      }
      .user-address-info-zipplus {
        max-width: 100px;
      }
      .user-address-info-gray{
        background: lightgray;
      }
    }
  }
}
.orange-bg {
    background-color: orange !important;
}
.voter-reg {
    label {
        display: block;
        margin-left: 1rem;
    }
    input {
        float: left;
        height: 20px;  
    }
}
button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
    margin-right: 1em;
    }
    &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
    }
}
@media only screen and (max-width: 510px) {
  .app-address-info{
    .app-input-address{
      display: block;
      .app-user-address-info{
        display: block;
      }
      .app-user-address{
        .user-address-info{
          min-width: 300px;
        }
        .user-address-info-city {
          width: 200px;
        }
        .zip-selection {
          width: 106px !important;
        }
        .user-address-info-zipplus {
          max-width: 100px;
        }
      }
    }
  }
}
</style>